// =============================================================================
// components/KpiRules/KpiRulesPage.jsx
// Main page for managing KPI anomaly detection rules.
//
// FIX — added RulesHelpPanel: a collapsible "theory" section explaining the
// core concepts (baseline, detection method, direction, SLA, volume floor,
// severity, quarantine, incident lifecycle) so an operator understands what
// each field on the rule form actually does before configuring rules.
// Collapsed by default — reference material, not a blocking wizard.
// =============================================================================
import { useEffect } from 'react';
import { Box, Grid, Paper, Typography, Alert, Snackbar } from '@mui/material';
import TuneIcon from '@mui/icons-material/Tune';

import { useKpiRules } from '../../hooks/useKpi';
import KpiRuleList    from './KpiRuleList';
import KpiRuleForm    from './KpiRuleForm';
import EngineLog      from './EngineLog';
import RulesHelpPanel from './RulesHelpPanel';

export default function KpiRulesPage() {
  const {
    items, loading,
    selectedRule, saving, saveError, saveSuccess,
    validation, rulesByTech,
    load, save, remove, select, openNew, validate, clearVal,
  } = useKpiRules();

  useEffect(() => { load(); }, []); // eslint-disable-line

  const handleCancel = () => { select(null); clearVal(); };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header --------------------------------------------------------- */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
        <TuneIcon color="primary" />
        <Box>
          <Typography variant="h5" fontWeight={700}>
            KPI Detection Rules
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Define, per KPI, which data the engine reads from and what criteria it uses to open incidents.
          </Typography>
        </Box>
      </Box>

      <RulesHelpPanel />

      <Grid container spacing={2.5}>
        {/* Left column — rule list -------------------------------------- */}
        <Grid item xs={12} md={4}>
          <Paper variant="outlined" sx={{ height: '78vh', display: 'flex', flexDirection: 'column' }}>
            <KpiRuleList
              rulesByTech={rulesByTech}
              totalCount={items.length}
              loading={loading}
              selectedId={selectedRule?.kpiId}
              onSelect={select}
              onCreate={openNew}
            />
          </Paper>
        </Grid>

        {/* Right column — form + engine history --------------------------- */}
        <Grid item xs={12} md={8}>
          <Paper variant="outlined" sx={{ p: 3, mb: 2.5 }}>
            {selectedRule ? (
              <KpiRuleForm
                rule={selectedRule}
                saving={saving}
                saveError={saveError}
                validation={validation}
                onSave={save}
                onCancel={handleCancel}
                onValidate={validate}
                onDelete={remove}
              />
            ) : (
              <Box sx={{ textAlign: 'center', py: 6 }}>
                <TuneIcon sx={{ fontSize: 32, color: 'text.disabled', mb: 1 }} />
                <Typography variant="body2" color="text.secondary">
                  Select a rule on the left to edit it, or click "New Rule".
                </Typography>
              </Box>
            )}
          </Paper>

          <EngineLog />
        </Grid>
      </Grid>

      <Snackbar
        open={saveSuccess}
        autoHideDuration={3000}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity="success" variant="filled">Rule saved successfully.</Alert>
      </Snackbar>
    </Box>
  );
}